import { APP_PATHS } from '@/constant/app-paths';

/**
 * A mapping of application routes to their corresponding i18n translation module names.
 * Each route is mapped to an array of module names that should be loaded for that route.
 *
 * @type {Record<string, string[]>}
 * @property {string[]} [key] - The application route path
 * @property {string[]} [value] - Array of module names to load for the route
 *                                Always includes 'generic' as the base module
 *                                followed by route-specific module names
 */
export const routeModuleMap: Record<string, string[]> = {
  // Must match the backend UILM module names the Angular app registers
  // (TranslationResourceModelV2 calls in its *.module.ts files) -- never
  // invent new module names here. Public/auth routes fall back to
  // LanguageProvider's `defaultModules` prop (src/App.tsx) when they have no
  // entry of their own here -- keep that prop in sync with 'generic', not a
  // typo'd/invented name, or every route below (and any route missing from
  // this map) silently stops picking up language changes.
  '/login': ['generic', 'auth'],
  '/oidc/activate': ['generic', 'auth'],
  '/activate': ['generic', 'auth'],
  '/success': ['generic', 'auth'],
  '/activate-failed': ['generic', 'auth'],
  // getMatchedModules() (language-context.tsx) does a longest-prefix match,
  // so each page can (and should) get its own entry here instead of being
  // collapsed into one catch-all per menu group -- that keeps every route only
  // loading the modules it actually needs, mirroring how the Angular app
  // registers translations per feature module (its
  // TranslationResourceModelV2(...) calls). The bare group entries at the end
  // are just the fallback for a route of that group not (yet) listed.
  // Paths come from APP_PATHS; the old /hr/* URLs only redirect, so they need none.
  [APP_PATHS.MY_PROFILE]: ['generic', 'uam', 'profiles', 'kpi-management'],

  // Home's widgets show leave, TOIL and timesheet figures.
  [APP_PATHS.HOME]: [
    'generic',
    'attendance',
    'leave-management',
    'toil-management',
    'timesheet-management',
    'lunch-system',
  ],
  [APP_PATHS.PEOPLE]: ['generic', 'uam'],

  [APP_PATHS.TIME_TRACKER]: ['generic', 'timesheet-management'],
  [APP_PATHS.ATTENDANCE]: ['generic', 'attendance', 'overtime-management'],
  [APP_PATHS.TEAM_ATTENDANCE]: ['generic', 'attendance', 'overtime-management'],
  [APP_PATHS.TEAM_TIMESHEETS]: ['generic', 'timesheet-management'],

  [APP_PATHS.MY_TIME_OFF]: ['generic', 'leave-management', 'toil-management'],
  [APP_PATHS.TEAM_TIME_OFF]: ['generic', 'leave-management', 'toil-management'],
  [APP_PATHS.TIME_OFF_CALENDAR]: ['generic', 'leave-management', 'toil-management'],
  [APP_PATHS.SICK_LEAVE]: ['generic', 'leave-management', 'toil-management'],

  [APP_PATHS.MY_REVIEWS]: ['generic', 'performance-management'],
  [APP_PATHS.ALL_REVIEWS]: ['generic', 'performance-management'],
  [APP_PATHS.PERFORMANCE_DASHBOARD]: ['generic', 'performance-management'],

  [APP_PATHS.CANDIDATES]: ['generic', 'profiles'],
  [APP_PATHS.OFFERS]: ['generic', 'profiles'],

  [APP_PATHS.ALL_SURVEYS]: ['generic', 'performance-management', 'survey'],
  [APP_PATHS.MY_SURVEY_RESPONSES]: ['generic', 'performance-management', 'survey'],

  [APP_PATHS.ORDER_MEAL]: ['generic', 'lunch-system'],
  [APP_PATHS.MEAL_ORDERS]: ['generic', 'lunch-system'],
  [APP_PATHS.MEAL_MENU]: ['generic', 'lunch-system'],

  [APP_PATHS.SERVICE_REQUESTS]: ['generic', 'service-desk'],

  [APP_PATHS.DEPARTMENTS]: ['generic', 'uam', 'department-manager'],
  [APP_PATHS.DESIGNATIONS]: ['generic', 'designation-manager'],
  [APP_PATHS.LOCATIONS]: ['generic', 'location-manager'],
  // Kosovo Projects and MBM Projects tabs (Phase 8).
  [APP_PATHS.PROJECTS]: ['generic', 'uam', 'project', 'timesheet-management'],
  [APP_PATHS.MBM_PROJECTS]: ['generic', 'timesheet-management'],
  [APP_PATHS.LEAVE_POLICIES]: ['generic', 'leave-management'],
  [APP_PATHS.HOLIDAYS]: ['generic', 'leave-management'],
  [APP_PATHS.ATTENDANCE_RULES]: ['generic', 'attendance'],
  [APP_PATHS.REVIEW_TEMPLATES]: ['generic', 'performance-management'],
  // The Angular kpi-configuration feature's strings live in 'performance-management' (KPI_CONFIG_TITLE,
  // MAX, ...) and its toast/validation strings in 'kpi-management', so both modules are needed here.
  [APP_PATHS.KPIS]: ['generic', 'kpi-management', 'performance-management'],
  [APP_PATHS.SKILLS]: ['generic', 'kpi-management'],
  [APP_PATHS.SURVEY_FORMS]: ['generic', 'performance-management', 'survey'],
  [APP_PATHS.CTC_LEVELS]: ['generic', 'uam'],
  [APP_PATHS.NOC_TYPES]: ['generic', 'service-desk'],
  [APP_PATHS.ROLES]: ['generic', 'role-manager'],
  [APP_PATHS.AUDIT_LOG]: ['generic', 'logs'],

  '/time-attendance': ['generic'],
  '/time-off': ['generic'],
  '/performance': ['generic'],
  '/hiring': ['generic'],
  '/surveys': ['generic'],
  '/meal': ['generic'],
  [APP_PATHS.SETTINGS]: ['generic'],
};
